/**
 * Celeste Skin - Base Component Factory
 *
 * Creates celeste-themed color-specific base components using the celeste-specific
 * factory (gradient-based colors, not flat). Does NOT use createColoredBase from
 * createBaseVariants — the celeste SVG structure is different from basics.
 */

import React from 'react';
import type { ColoredBaseProps } from '../../constants/types';
import CelesteBaseComponent from './CelesteBaseComponent';
import { CELESTE_COLOR_SCHEMES, type CelesteColorScheme } from './constants/baseColorSchemes';
import { RedPawn, BluePawn, YellowPawn, GreenPawn } from '../../pawns/celeste/pawnFactory';
import type { PawnProps } from '../../constants/types';

function createCelesteBase(
  color: string,
  colorScheme: CelesteColorScheme,
  PawnComponent: React.ComponentType<PawnProps>
) {
  const displayName = `Celeste${color.charAt(0).toUpperCase() + color.slice(1)}Base`;

  const Base = React.memo(function CelesteBase(props: ColoredBaseProps) {
    return (
      <CelesteBaseComponent
        {...props}
        colorScheme={colorScheme}
        PawnComponent={props.PawnComponent ?? PawnComponent}
      />
    );
  });

  Base.displayName = displayName;
  return Base;
}

export const RedBase = createCelesteBase('red', CELESTE_COLOR_SCHEMES.red, RedPawn);
export const BlueBase = createCelesteBase('blue', CELESTE_COLOR_SCHEMES.blue, BluePawn);
export const YellowBase = createCelesteBase('yellow', CELESTE_COLOR_SCHEMES.yellow, YellowPawn);
export const GreenBase = createCelesteBase('green', CELESTE_COLOR_SCHEMES.green, GreenPawn);

export type { ColoredBaseProps as BlueBaseProps };
export type { ColoredBaseProps as RedBaseProps };
export type { ColoredBaseProps as GreenBaseProps };
export type { ColoredBaseProps as YellowBaseProps };
